import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { EntityLocations, LocationDTO } from '@sales-center/common'
import {
  loadEntityLocations,
  deleteEntityLocation,
  addEditEntityLocation,
  addServiceLocations,
} from './api'
import { StoreData } from '../reducers/typings'

const billToAdapter = createEntityAdapter<LocationDTO>()
const serviceAdapter = createEntityAdapter<LocationDTO>()

/**
 * `latestRequestedEntityId` is the entityId of the most recently dispatched
 * loadEntityLocations request, so a slower, superseded response for a previous
 * entity is ignored in `.fulfilled` instead of repopulating the wrong data
 * (WWM-18150).
 */
export const initialState = {
  billToLocations: billToAdapter.getInitialState(),
  serviceLocations: serviceAdapter.getInitialState(),
  latestRequestedEntityId: null as number | null,
}

export const entityLocationsSlice = createSlice({
  name: 'entityLocationsSlice',
  initialState,
  reducers: {
    setEntityLocations: (
      state,
      { payload }: PayloadAction<EntityLocations>,
    ) => {
      const { billToLocations, serviceLocations } = payload

      billToAdapter.setAll(state.billToLocations, billToLocations)
      serviceAdapter.setAll(state.serviceLocations, serviceLocations)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadEntityLocations.pending, (state, { meta }) => {
      if (!meta.arg.updateExisting) {
        // WWM-18150 - only genuine entity-switch loads participate in stale-response
        // detection below. updateExisting:true (see matchCreateNew) is an explicit "merge
        // into whatever's already loaded for the current entity" refresh, not a switch to a
        // different entity, so it's kept orthogonal to this tracking entirely.
        state.latestRequestedEntityId = meta.arg.entityId
        billToAdapter.removeAll(state.billToLocations)
        serviceAdapter.removeAll(state.serviceLocations)
      }
    })

    builder.addCase(
      loadEntityLocations.fulfilled,
      (state, { payload, meta }) => {
        // WWM-18150 - if the user has since switched to a different opportunity/lead, a
        // slower-resolving request for the PREVIOUS entity can still complete after the new
        // one already cleared the store in .pending above. Applying this stale response would
        // silently repopulate serviceLocations with the wrong entity's location(s) - which the
        // Add Service form's single-location auto-default would then use for a brand new
        // product on the CURRENT (different) entity. Discard it instead - but only for genuine
        // entity-switch loads; updateExisting:true refreshes always apply, same as before.
        if (
          !meta.arg.updateExisting &&
          meta.arg.entityId !== state.latestRequestedEntityId
        ) {
          return
        }

        const { billToLocations, serviceLocations } = payload

        billToAdapter.upsertMany(state.billToLocations, billToLocations)
        serviceAdapter.upsertMany(state.serviceLocations, serviceLocations)
      },
    )

    builder.addCase(addServiceLocations.fulfilled, (state, { payload }) => {
      const { serviceLocations } = payload
      serviceAdapter.upsertMany(state.serviceLocations, serviceLocations)
    })

    builder.addCase(
      addEditEntityLocation.fulfilled,
      (state, { payload, meta: { arg } }) => {
        if (arg.billingLocation) {
          billToAdapter.upsertOne(state.billToLocations, payload)
        } else {
          serviceAdapter.upsertOne(state.serviceLocations, payload)

          if (arg.updateBillTo) {
            const billTo = billToAdapter
              .getSelectors()
              .selectAll(state.billToLocations)
              .pop()

            if (billTo) {
              if (payload.externalLinkId) {
                billToAdapter.updateOne(state.billToLocations, {
                  id: billTo.id,
                  changes: {
                    ...billTo,
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    company: payload.company,
                    phoneNumber: payload.phoneNumber,
                    phoneExt: payload.phoneExt,
                    altPhoneExt: payload.altPhoneExt,
                    altPhoneNumber: payload.altPhoneNumber,
                    emailAddresses: payload.emailAddresses,
                  },
                })
              } else {
                billToAdapter.updateOne(state.billToLocations, {
                  id: billTo.id,
                  changes: {
                    ...payload,
                    externalLinkId: billTo.externalLinkId,
                    externalLinkType: billTo.externalLinkType,
                    locationCode: billTo.locationCode,
                    id: billTo.id,
                    billingLocation: true,
                  },
                })
              }
            }
          }
        }
      },
    )

    builder.addCase(deleteEntityLocation.fulfilled, (state, { payload }) => {
      billToAdapter.removeOne(state.billToLocations, payload.id)
      serviceAdapter.removeOne(state.serviceLocations, payload.id)
    })
  },
})

export default entityLocationsSlice.reducer

export const { setEntityLocations } = entityLocationsSlice.actions

export const { selectAll: selectAllBillToLocations } =
  billToAdapter.getSelectors<StoreData>(
    (state) => state.entityLocations.billToLocations,
  )
export const { selectAll: selectAllServiceLocations } =
  serviceAdapter.getSelectors<StoreData>(
    (state) => state.entityLocations.serviceLocations,
  )

export const selectAllEntityLocations = (state: StoreData) => ({
  billToLocations: selectAllBillToLocations(state),
  serviceLocations: selectAllServiceLocations(state),
})
